<template>
  <div class="gestione-reclami">
    <div class="header-section">
      <h2 class="page-title">Storico Reclami</h2>
      <p class="subtitle">Storico e gestione dei reclami</p>
    </div>

    <div class="content-section">
      <div v-if="loading" class="loading-section">
        <div class="spinner"></div>
        <p>Caricamento reclami in corso...</p>
      </div>

      <div v-else class="reclami-list">
        <div v-if="reclami.length === 0" class="no-data">
          <p>Nessun reclamo trovato</p>
        </div>

        <div v-for="reclamo in reclami" :key="reclamo.id" class="reclamo-card">
          <div class="reclamo-header">
            <div class="reclamo-info">
              <div class="title-status">
                <h4 class="reclamo-titolo">{{ getTitolo(reclamo) }}</h4>
                <span class="status-badge" :class="getStatoBadgeClass(reclamo)">
                  {{ getStatoLabel(reclamo) }}
                </span>
              </div>
              <div class="meta-info">
                <span class="categoria">{{ getCategoriaDescrizione(reclamo) }}</span>
                <span class="data">{{ formatData(reclamo.created_at) }}</span>
              </div>
              <p class="descrizione">{{ getDescrizioneBreve(reclamo.descrizione) }}</p>
            </div>

            <!-- Bottoni azione -->
            <div class="reclamo-actions">
              <button v-if="!reclamo.fine" class="action-btn close-btn" @click="chiudiReclamo(reclamo.id)"
                :disabled="updatingReclamo === reclamo.id">
                {{ updatingReclamo === reclamo.id ? 'Chiudendo...' : 'Chiudi' }}
              </button>
              <button v-else class="action-btn open-btn" @click="apriReclamo(reclamo.id)"
                :disabled="updatingReclamo === reclamo.id">
                {{ updatingReclamo === reclamo.id ? 'Aprendo...' : 'Apri' }}
              </button>

              <button class="action-btn detail-btn" @click="toggleDettagli(reclamo.id)">
                {{ reclamo.showDetails ? '▲ Dettagli' : '▼ Dettagli' }}
              </button>
            </div>
          </div>
          <div v-if="reclamo.showDetails" class="reclamo-dettagli">
            <div class="dettagli-grid">
              <div class="dettaglio-item">
                <strong>Utente:</strong>
                <span>{{ reclamo.utente || 'N/A' }}</span>
              </div>
              <div class="dettaglio-item">
                <strong>Nome Servizio:</strong>
                <span>{{ reclamo.nome_servizio || 'N/A' }}</span>
              </div>
              <div class="dettaglio-item">
                <strong>Numero Fatture:</strong>
                <span>{{ reclamo.n_fatture || 'N/A' }}</span>
              </div>
              <div class="dettaglio-item">
                <strong>Data Inizio Gestione:</strong>
                <span>{{ formatDataCompleta(reclamo.inizio) }}</span>
              </div>
              <div class="dettaglio-item">
                <strong>Data Fine Gestione:</strong>
                <span>{{ reclamo.fine ? formatDataCompleta(reclamo.fine) : 'In corso' }}</span>
              </div>
              <div class="dettaglio-item">
                <strong>Codice Cliente:</strong>
                <span>{{ reclamo.cod_cliente || 'N/A' }}</span>
              </div>
              <div class="dettaglio-item">
                <strong>Codice Agente:</strong>
                <span>{{ reclamo.cod_agente || 'N/A' }}</span>
              </div>
              <div class="dettaglio-item">
                <strong>Codice Rapp:</strong>
                <span>{{ reclamo.cod_rapp || 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <router-view />
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import axios from 'axios';

export default {
  name: 'StoricoReclami',
  setup() {
    const toast = useToast();
    const loading = ref(false);
    const updatingReclamo = ref(null);
    const savingNotes = ref(null);
    const reclami = ref([]);
    const categorie = ref([]);
    const sottocategorie = ref([]);

    const apiBaseUrl = process.env.VUE_APP_ASK_PRATICHE_URL;

    const caricaReclami = async () => {
      loading.value = true;
      try {
        await axios.get(`${apiBaseUrl}/api/reclami`).then((res)=>{
          reclami.value = res.data.data.map(reclamo => ({
            ...reclamo,
            showDetails: false,
            note_interne: reclamo.note_interne || ''
          }));
        })
      } catch (error) {
        console.error('Errore nel caricamento dei reclami:', error);
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i reclami dal database',
          life: 3000
        });
      } finally {
        loading.value = false;
      }
    };
    onMounted(async () => {
      await caricaReclami(); 
    });

    const formatData = (dataString) => {
      if (!dataString) return 'N/A';
      const data = new Date(dataString);
      return data.toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const formatDataCompleta = (dataString) => {
      if (!dataString) return 'N/A';
      const data = new Date(dataString);
      return data.toLocaleString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getTitolo = (reclamo) => {
      const categoria = getCategoriaDescrizione(reclamo);
      const descrizioneBreve = getDescrizioneBreve(reclamo.descrizione);
      return `${categoria} - ${descrizioneBreve}`;
    };

    const getCategoriaDescrizione = (reclamo) => {
      return reclamo.sotto_categoria?.categoria?.descrizione || 'Categoria sconosciuta';
    };

    const getDescrizioneBreve = (descrizione) => {
      if (!descrizione) return 'Nessuna descrizione';
      return descrizione.length > 100 ?
        descrizione.substring(0, 100) + '...' :
        descrizione;
    };

    const getStatoBadgeClass = (reclamo) => {
      return reclamo.fine ? 'status-chiuso' : 'status-aperto';
    };

    const getStatoLabel = (reclamo) => {
      return reclamo.fine ? 'CHIUSO' : 'APERTO';
    };

    const chiudiReclamo = async (id) => {
      updatingReclamo.value = id;
      try {
        const now = new Date().toISOString();
        await axios.put(`${apiBaseUrl}/api/reclami/${id}`, {
          fine: now
        });

        // Aggiorna il reclamo locale
        const reclamo = reclami.value.find(r => r.id === id);
        if (reclamo) {
          reclamo.fine = now;
        }

        toast.add({
          severity: 'success',
          summary: 'Reclamo Chiuso',
          detail: 'Il reclamo è stato chiuso con successo',
          life: 3000
        });
      } catch (error) {
        console.error('Errore nella chiusura del reclamo:', error);
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile chiudere il reclamo',
          life: 3000
        });
      } finally {
        updatingReclamo.value = null;
      }
    };

    const apriReclamo = async (id) => {
      updatingReclamo.value = id;
      try {
        await axios.put(`${apiBaseUrl}/api/reclami/${id}`, {
          fine: null
        });

        // Aggiorna il reclamo locale
        const reclamo = reclami.value.find(r => r.id === id);
        if (reclamo) {
          reclamo.fine = null;
        }

        toast.add({
          severity: 'info',
          summary: 'Reclamo Riaperto',
          detail: 'Il reclamo è stato riaperto',
          life: 3000
        });
      } catch (error) {
        console.error('Errore nella riapertura del reclamo:', error);
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile riaprire il reclamo',
          life: 3000
        });
      } finally {
        updatingReclamo.value = null;
      }
    };

    const toggleDettagli = (id) => {
      const reclamo = reclami.value.find(r => r.id === id);
      if (reclamo) {
        reclamo.showDetails = !reclamo.showDetails;
      }
    };

    const salvaNote = async (id) => {
      savingNotes.value = id;
      try {
        const reclamo = reclami.value.find(r => r.id === id);
        if (reclamo) {
          await axios.put(`${apiBaseUrl}/api/reclami/${id}`, {
            note_interne: reclamo.note_interne
          });

          toast.add({
            severity: 'success',
            summary: 'Note Salvate',
            detail: 'Le note interne sono state salvate',
            life: 3000
          });
        }
      } catch (error) {
        console.error('Errore nel salvataggio delle note:', error);
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile salvare le note',
          life: 3000
        });
      } finally {
        savingNotes.value = null;
      }
    };

    return {
      loading,
      updatingReclamo,
      savingNotes,
      reclami,
      formatData,
      formatDataCompleta,
      getTitolo,
      getCategoriaDescrizione,
      getDescrizioneBreve,
      getStatoBadgeClass,
      getStatoLabel,
      chiudiReclamo,
      apriReclamo,
      toggleDettagli,
      salvaNote
    };
  }
}
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.gestione-reclami {
  font-family: 'Inter', sans-serif;
  padding: 2rem;
  min-height: 100vh;
  width: 100%;
  max-width: none;
  margin: 0;
}

.header-section {
  margin-bottom: 2.5rem;
  text-align: left;
}

.page-title {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.subtitle {
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 400;
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  gap: 1.5rem;
}

.spinner {
  border: 3px solid #e2e8f0;
  border-top: 3px solid #05b4a5;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data {
  text-align: center;
  padding: 4rem;
  color: #64748b;
  font-size: 1.2rem;
  font-weight: 500;
}

.content-section {
  width: 100%;
  max-width: none;
}

.reclami-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: none;
}

.reclamo-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 2rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.reclamo-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #215159 0%, #05b4a5 100%);
}

.reclamo-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(33, 81, 89, 0.1);
  border-color: #05b4a5;
}

.reclamo-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
}

.reclamo-info {
  flex: 1;
}

.title-status {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.reclamo-titolo {
  margin: 0;
  color: #215159;
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 1.3;
}

.status-badge {
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-aperto {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
}

.status-chiuso {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #34d399;
}

.meta-info {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: center;
}

.categoria {
  background: #215159;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.4rem 0.8rem;
  border-radius: 12px;
}

.data {
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
  background: #f1f5f9;
  padding: 0.4rem 0.8rem;
  border-radius: 12px;
}

.descrizione {
  color: #475569;
  line-height: 1.6;
  margin: 0;
  font-size: 1rem;
}

.reclamo-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 120px;
}

.action-btn {
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.close-btn {
  background: #ef4444;
  color: #ffffff;
}

.close-btn:hover:not(:disabled) {
  background: #dc2626;
}

.open-btn {
  background: #05b4a5;
  color: #ffffff;
}

.open-btn:hover:not(:disabled) {
  background: #059890;
}

.detail-btn {
  background: #215159;
  color: #ffffff;
}

.detail-btn:hover:not(:disabled) {
  background: #1a434a;
}

.reclamo-dettagli {
  margin-top: 2rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 2rem;
  background: #f8fafc;
  border-radius: 12px;
  padding: 2rem;
}

.dettagli-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.dettaglio-item {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 1rem;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.dettaglio-item.full-width {
  grid-column: 1 / -1;
}

.dettaglio-item strong {
  color: #215159;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dettaglio-item span {
  color: #475569;
  font-size: 1rem;
  font-weight: 400;
}

.descrizione-completa {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 3px solid #05b4a5;
  margin: 0.5rem 0;
  line-height: 1.6;
  font-size: 1rem;
  color: #334155;
}

.note-textarea {
  width: 100%;
  padding: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: inherit;
  resize: vertical;
  margin-top: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  background: #ffffff;
}

.note-textarea:focus {
  outline: none;
  border-color: #05b4a5;
  box-shadow: 0 0 0 3px rgba(5, 180, 165, 0.1);
}

.dettagli-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .gestione-reclami {
    padding: 1rem;
  }

  .page-title {
    font-size: 2rem;
  }

  .reclamo-header {
    flex-direction: column;
    gap: 1.5rem;
  }

  .reclamo-actions {
    flex-direction: row;
    min-width: auto;
    width: 100%;
  }

  .action-btn {
    flex: 1;
  }

  .meta-info {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .reclamo-card {
    padding: 1.5rem;
  }

  .dettagli-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .title-status {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>